import React, { useEffect, useMemo, useState } from "react";
import { Cookies } from "react-cookie";
import { Link, useNavigate } from "react-router-dom";
import moment from "moment-timezone";
import RetreatApi from "../../api/retreatApi";
import { trainingGuides } from "../../data/trainingGuides";
import { useTrainingGuide } from "../../components/AdminTrainingGuide";
import "../../assets/css/HomeDashboard.css";

const retreatApi = new RetreatApi();
const STATUS_COLORS = {
  upcoming: "#8b1e3f",
  past: "#1f3bb3",
  closed: "#1f7a4d",
  inactive: "#94a3b8",
};

const cap = (value) => {
  const text = String(value || "").trim();
  if (!text) return "";
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const titleLabel = (value) =>
  String(value || "")
    .split(" ")
    .map((word, index) => {
      if (!word) return "";
      if (index > 0 && ["and", "of", "the"].includes(word.toLowerCase())) {
        return word.toLowerCase();
      }
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");

const formatDateRange = (from, to) => {
  if (!from || !to) return "Dates not set";
  const start = moment(from).tz("America/New_York");
  const end = moment(to).tz("America/New_York");
  if (!start.isValid() || !end.isValid()) return "Dates not set";
  if (start.isSame(end, "day")) return start.format("MMM D, YYYY");
  if (start.isSame(end, "month")) {
    return `${start.format("MMM D")} – ${end.format("D, YYYY")}`;
  }
  if (start.isSame(end, "year")) {
    return `${start.format("MMM D")} – ${end.format("MMM D, YYYY")}`;
  }
  return `${start.format("MMM D, YYYY")} – ${end.format("MMM D, YYYY")}`;
};

const startDateParts = (from) => {
  const start = moment(from).tz("America/New_York");
  if (!from || !start.isValid()) return { month: "—", day: "—" };
  return { month: start.format("MMM"), day: start.format("D") };
};

const GUIDE_TOPIC_ICONS = {
  Retreats: "mdi-church",
  "Retreat Closures": "mdi-check-circle-outline",
  Closures: "mdi-check-circle-outline",
  "Retreat Roster": "mdi-clipboard-text-outline",
  Couples: "mdi-account-heart",
};

const chartRetreatItems = (names = []) =>
  (Array.isArray(names) ? names : []).map((item) =>
    item && typeof item === "object"
      ? { id: item.id, name: item.name || "Retreat" }
      : { id: null, name: String(item || "Retreat") },
  );

const retreatTitle = (retreat) => {
  const parish = retreat.parishName || retreat.title || "Retreat";
  const language = retreat.language ? ` (${cap(retreat.language)})` : "";
  const location = retreat.location ? ` ${cap(retreat.location)}` : "";
  return `${parish}${language}${location}`.trim();
};

const todayKey = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
};

function smoothPath(points) {
  if (!points.length) return "";
  if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;
  let d = `M ${points[0].x} ${points[0].y}`;
  for (let i = 0; i < points.length - 1; i += 1) {
    const p0 = points[i === 0 ? i : i - 1];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2] || p2;
    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
  }
  return d;
}

function MonthLineChart({ series, selected, onSelect }) {
  const [hover, setHover] = useState("");
  const width = 640;
  const height = 236;
  const pad = { top: 22, right: 18, bottom: 36, left: 38 };
  const chartW = width - pad.left - pad.right;
  const chartH = height - pad.top - pad.bottom;
  const max = Math.max(...series.map((item) => item.retreats), 1);
  const ticks = Array.from({ length: 5 }, (_, i) => Math.round((max / 4) * i));
  const uniqueTicks = [...new Set(ticks)];
  const points = series.map((item, index) => {
    const x =
      pad.left + (series.length === 1 ? chartW / 2 : (index / (series.length - 1)) * chartW);
    const y = pad.top + chartH - (item.retreats / max) * chartH;
    return { ...item, x, y };
  });
  const line = smoothPath(points);
  const area = points.length
    ? `${line} L ${points[points.length - 1].x} ${pad.top + chartH} L ${points[0].x} ${
        pad.top + chartH
      } Z`
    : "";
  const hoverItem = points.find((item) => item.month === hover) || null;
  const selectedItem = points.find((item) => item.month === selected) || null;
  const currentMonth = todayKey().slice(0, 7);
  const tipLeft = hoverItem
    ? Math.min(Math.max((hoverItem.x / width) * 100, 18), 82)
    : 50;

  return (
    <div className="hd-line">
      <div
        className="hd-line-chart"
        onMouseLeave={() => setHover("")}
        role="group"
        aria-label="Retreats by start month"
      >
        <svg viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
          <defs>
            <linearGradient id="hd-line-fill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#8b1e3f" stopOpacity="0.2" />
              <stop offset="100%" stopColor="#8b1e3f" stopOpacity="0.02" />
            </linearGradient>
            <linearGradient id="hd-line-stroke" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#8b1e3f" />
              <stop offset="100%" stopColor="#c45274" />
            </linearGradient>
          </defs>
          {uniqueTicks.map((tick) => {
            const y = pad.top + chartH - (tick / max) * chartH;
            return (
              <g key={`tick-${tick}`}>
                <line
                  x1={pad.left}
                  x2={width - pad.right}
                  y1={y}
                  y2={y}
                  className="hd-line-grid"
                />
                <text x={pad.left - 8} y={y + 3} textAnchor="end" className="hd-line-axis">
                  {tick}
                </text>
              </g>
            );
          })}
          {hoverItem ? (
            <line
              x1={hoverItem.x}
              x2={hoverItem.x}
              y1={pad.top}
              y2={pad.top + chartH}
              className="hd-line-guide"
            />
          ) : null}
          {area ? <path d={area} fill="url(#hd-line-fill)" /> : null}
          {line ? (
            <path d={line} fill="none" stroke="url(#hd-line-stroke)" className="hd-line-path" />
          ) : null}
        </svg>
        {points.map((item) => {
          const left = (item.x / width) * 100;
          const top = (item.y / height) * 100;
          const isSelected = selected === item.month;
          const isHover = hover === item.month;
          const isCurrent = item.month === currentMonth;
          return (
            <button
              key={item.month}
              type="button"
              className={`hd-line-dot${isSelected ? " is-selected" : ""}${
                isHover ? " is-hover" : ""
              }${isCurrent ? " is-current" : ""}${
                item.retreats === 0 ? " is-zero" : ""
              }`}
              style={{ left: `${left}%`, top: `${top}%` }}
              onMouseEnter={() => setHover(item.month)}
              onFocus={() => setHover(item.month)}
              onClick={() => onSelect(isSelected ? "" : item.month)}
              aria-pressed={isSelected}
              aria-label={`${item.label}: ${item.retreats} retreat${
                item.retreats === 1 ? "" : "s"
              }`}
            >
              <span className="hd-line-value">{item.retreats}</span>
            </button>
          );
        })}
        {points.map((item) => (
          <span
            key={`${item.month}-label`}
            className={`hd-line-month${item.month === currentMonth ? " is-current" : ""}`}
            style={{ left: `${(item.x / width) * 100}%` }}
          >
            {item.shortLabel}
          </span>
        ))}
        {hoverItem ? (
          <div
            className="hd-line-tip"
            onMouseEnter={() => setHover(hoverItem.month)}
            style={{
              left: `${tipLeft}%`,
              top: `${Math.max((hoverItem.y / height) * 100 - 8, 8)}%`,
            }}
          >
            <strong>
              {hoverItem.label}: {hoverItem.retreats} retreat
              {hoverItem.retreats === 1 ? "" : "s"}
            </strong>
            {chartRetreatItems(hoverItem.names).length ? (
              <ul>
                {chartRetreatItems(hoverItem.names)
                  .slice(0, 6)
                  .map((retreat, index) => (
                    <li key={`${retreat.id || retreat.name}-${index}`}>
                      {retreat.id ? (
                        <Link to={`/retreatRoster?retreatId=${retreat.id}`}>
                          {retreat.name}
                        </Link>
                      ) : (
                        retreat.name
                      )}
                    </li>
                  ))}
              </ul>
            ) : (
              <span>No retreats start this month.</span>
            )}
            {chartRetreatItems(hoverItem.names).length > 6 ? (
              <span>+{chartRetreatItems(hoverItem.names).length - 6} more</span>
            ) : null}
          </div>
        ) : null}
      </div>
      {selectedItem ? (
        <div className="hd-line-detail">
          <strong>
            {selectedItem.label}: {selectedItem.retreats} retreat
            {selectedItem.retreats === 1 ? "" : "s"}
          </strong>
          {chartRetreatItems(selectedItem.names).length ? (
            <ul>
              {chartRetreatItems(selectedItem.names).map((retreat, index) => (
                <li key={`${retreat.id || retreat.name}-${index}`}>
                  {retreat.id ? (
                    <Link to={`/retreatRoster?retreatId=${retreat.id}`}>
                      {retreat.name}
                    </Link>
                  ) : (
                    retreat.name
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <span>No retreats start this month.</span>
          )}
        </div>
      ) : (
        <p className="hd-line-hint">
          Hover a point to see retreat names. Click a month to filter Upcoming Retreats.
        </p>
      )}
    </div>
  );
}

function closureChecks(retreat) {
  return [
    { key: "roster", label: "Roster", done: Boolean(retreat.rosterConfirmed) },
    { key: "directory", label: "Directory", done: Boolean(retreat.directoryConfirmed) },
    { key: "program", label: "Program", done: Boolean(retreat.programConfirmed) },
    { key: "pictures", label: "Pictures", done: Boolean(retreat.picturesConfirmed) },
    { key: "closed", label: "Closed", done: Boolean(retreat.retreatCloseStatus) },
  ];
}

function NeedsClosingList({ retreats }) {
  if (!retreats.length) {
    return (
      <div className="hd-close-empty">
        <p>Recent finished retreats are all closed.</p>
        <Link to="/retreat-closure-list">Open Retreat Closures</Link>
      </div>
    );
  }

  return (
    <div className="hd-close-list">
      {retreats.slice(0, 3).map((retreat) => {
        const checks = closureChecks(retreat);
        const done = checks.filter((item) => item.done).length;
        return (
          <Link
            key={retreat.id}
            className="hd-close-item"
            to={`/retreatRoster?retreatId=${retreat.id}`}
          >
            <div className="hd-close-top">
              <div>
                <strong>{retreatTitle(retreat)}</strong>
                <span>{formatDateRange(retreat.dateFrom, retreat.dateTo)}</span>
              </div>
              <em>
                {done} of {checks.length}
              </em>
            </div>
            <span className="hd-close-bar" aria-hidden="true">
              <span
                className={done === checks.length ? "is-full" : ""}
                style={{ width: `${(done / checks.length) * 100}%` }}
              />
            </span>
            <div className="hd-close-pills">
              {checks.map((item) => (
                <span
                  key={item.key}
                  className={`hd-close-pill${item.done ? " is-done" : ""}`}
                >
                  <i
                    className={`mdi ${item.done ? "mdi-check" : "mdi-close"}`}
                    aria-hidden="true"
                  />
                  {item.label}
                </span>
              ))}
            </div>
          </Link>
        );
      })}
    </div>
  );
}

function StatusDonut({ slices, total, selected, onSelect }) {
  const sum = slices.reduce((count, slice) => count + slice.value, 0);
  let cursor = 0;
  const gradient = slices
    .map((slice) => {
      const start = (cursor / (sum || 1)) * 100;
      cursor += slice.value;
      const end = (cursor / (sum || 1)) * 100;
      return `${STATUS_COLORS[slice.key] || "#94a3b8"} ${start}% ${end}%`;
    })
    .join(", ");

  return (
    <div className="hd-donut-wrap">
      <div
        className="hd-donut"
        style={{ background: sum ? `conic-gradient(${gradient})` : "#eef2f6" }}
      >
        <div className="hd-donut-hole">
          <strong>{total}</strong>
          <span>Retreats</span>
        </div>
      </div>
      <div className="hd-mix">
        {slices.length === 0 ? (
          <p className="hd-card-note">Retreat status will appear here once retreats are added.</p>
        ) : (
          slices.map((slice) => (
            <button
              key={slice.key}
              type="button"
              className={selected === slice.key ? "is-selected" : ""}
              onClick={() => onSelect(selected === slice.key ? "" : slice.key)}
              aria-pressed={selected === slice.key}
            >
              <span>
                <i className="hd-swatch" style={{ background: STATUS_COLORS[slice.key] }} />
                {titleLabel(slice.label)}
              </span>
              <strong>{slice.value}</strong>
            </button>
          ))
        )}
      </div>
    </div>
  );
}

const Dashboard = () => {
  const cookies = new Cookies();
  const userData = cookies.get("LoveStrongUser") || null;
  const training = useTrainingGuide();
  const isAdmin = userData && userData.userRole === "ADMIN";
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [month, setMonth] = useState("");
  const [status, setStatus] = useState("");
  const navigate = useNavigate();

  const loadOverview = async () => {
    setLoading(true);
    setError("");
    const payload = { today: todayKey() };
    if (userData?.committeeMember === true && userData?.userRole !== "ADMIN") {
      payload.committeeId = userData.id;
    }
    if (userData?.access === true && userData?.userRole !== "ADMIN") {
      payload.accessId = userData.id;
    }
    const response = await retreatApi.getDashboardOverview(payload);
    if (response && response.success) {
      setOverview(response);
      setLoading(false);
      return;
    }
    setOverview(null);
    setError(response?.msg || "The dashboard could not be loaded.");
    setLoading(false);
  };

  useEffect(() => {
    loadOverview();
    // Load once for the signed-in user. Refresh is explicit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stats = overview?.stats || {};
  const shortcuts = [
    { to: "/quickReport", label: "Quick Reports", icon: "mdi-grid-large" },
    { to: "/search_data", label: "Advanced Search", icon: "mdi-magnify" },
    {
      to: "/manageCouples",
      label: isAdmin ? "Manage Couples Data" : "View Couples Data",
      icon: "mdi-account-multiple",
    },
    {
      to: "/manageRetreat",
      label: isAdmin ? "Upload Retreats" : "View Retreats",
      icon: "mdi-church",
    },
    ...(isAdmin
      ? [{ to: "/sendEmail", label: "Broadcast Emails", icon: "mdi-email-outline" }]
      : []),
    {
      href: "https://lovestrongmarriage.com/?page_id=6",
      label: "Help",
      icon: "mdi-help-circle-outline",
      external: true,
    },
  ];

  const statCards = [
    {
      key: "totalRetreats",
      label: "Total Retreats",
      hint: `${stats.activeRetreats || 0} Active · ${stats.inactiveRetreats || 0} Inactive`,
      icon: "mdi-church",
      to: "/manageRetreat",
      tone: "burgundy",
    },
    {
      key: "upcomingRetreats",
      label: "Upcoming Retreats",
      hint: `${stats.inProgressRetreats || 0} Happening Now`,
      icon: "mdi-calendar-clock",
      to: "/manageRetreat",
      tone: "gold",
    },
    {
      key: "totalCouples",
      label: "All Couples",
      hint: "Total couple records",
      icon: "mdi-account-multiple",
      to: "/manageCouples",
      tone: "navy",
    },
    {
      key: "closedRetreats",
      label: "Closed Retreats",
      hint: "Post-Retreat Closure",
      icon: "mdi-check-circle-outline",
      to: "/retreat-closure-list",
      tone: "green",
    },
    {
      key: "priests",
      label: "Priests",
      hint: `${stats.assignedPriests || 0} Assigned on Rosters`,
      icon: "mdi-account-tie",
      to: "/priest-list",
      tone: "teal",
    },
    {
      key: "parishes",
      label: "Parishes",
      hint: overview?.scoped ? "Parishes on your retreats" : "All parish records",
      icon: "mdi-map-marker",
      to: "/parish",
      tone: "slate",
    },
  ];

  const languageRows = (overview?.languages || []).filter(
    (item) => String(item.label || "").trim().toLowerCase() !== "not set",
  );
  const languageMax = Math.max(...languageRows.map((item) => item.value), 1);
  const retreatYear =
    overview?.retreatsByMonth?.[0]?.month?.slice(0, 4) || String(new Date().getFullYear());
  const retreatYearTotal = (overview?.retreatsByMonth || []).reduce(
    (sum, item) => sum + Number(item.retreats || 0),
    0,
  );

  const matchedRetreats = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return (overview?.upcoming || []).filter((retreat) => {
      if (month && String(retreat.dateFrom || "").slice(0, 7) !== month) return false;
      if (status === "inactive" && retreat.status !== "INACTIVE") return false;
      if (status === "closed" && !retreat.retreatCloseStatus) return false;
      if (status === "upcoming" && (retreat.status !== "ACTIVE" || retreat.retreatCloseStatus)) {
        return false;
      }
      if (status === "past") return false;
      if (!needle) return true;
      const haystack = [
        String(retreat.id || ""),
        retreatTitle(retreat),
        retreat.parishName,
        retreat.parishCity,
        retreat.location,
        retreat.language,
        retreat.status,
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(needle);
    });
  }, [overview, query, month, status]);

  const visibleRetreats = useMemo(() => {
    if (query.trim() || month || status) return matchedRetreats;
    return matchedRetreats.slice(0, 2);
  }, [matchedRetreats, query, month, status]);

  const filtersActive = Boolean(query || month || status);
  const firstName = userData?.firstName || userData?.hisFirstName || "";

  return (
    <div className="hd-page">
      <header className="hd-header">
        <div>
          <p className="hd-kicker">LoveStrong</p>
          <h1>{firstName ? `Welcome back, ${firstName}` : "Dashboard"}</h1>
          <p className="hd-lead">
            Retreat activity, couple registration, and the work still open on upcoming retreats.
          </p>
        </div>
        <div className="hd-header-side">
          <span className="hd-date">{moment().tz("America/New_York").format("dddd, MMM D, YYYY")}</span>
          <button type="button" className="hd-refresh" onClick={loadOverview} disabled={loading}>
            <i className="mdi mdi-refresh" aria-hidden="true" />
            Refresh
          </button>
        </div>
      </header>

      <nav className="hd-shortcuts" aria-label="Dashboard shortcuts">
        {shortcuts.map((item) =>
          item.external ? (
            <a
              key={item.label}
              className="hd-shortcut"
              href={item.href}
              target="_blank"
              rel="noreferrer"
            >
              <i className={`mdi ${item.icon}`} aria-hidden="true" />
              {item.label}
            </a>
          ) : (
            <Link key={item.label} className="hd-shortcut" to={item.to}>
              <i className={`mdi ${item.icon}`} aria-hidden="true" />
              {item.label}
            </Link>
          ),
        )}
      </nav>

      {loading ? (
        <div className="hd-stats" aria-hidden="true">
          {statCards.map((card) => (
            <div key={card.key} className="hd-skeleton" />
          ))}
        </div>
      ) : null}

      {error && !loading ? (
        <div className="hd-error" role="alert">
          <p>{error}</p>
          <button type="button" className="hd-refresh" onClick={loadOverview}>
            Try again
          </button>
        </div>
      ) : null}

      {overview && !loading ? (
        <>
          <section className="hd-stats" aria-label="Key statistics">
            {statCards.map((card) => {
              const isTotalRetreats = card.key === "totalRetreats";
              const body = (
                <>
                  <i className={`hd-stat-icon mdi ${card.icon}`} aria-hidden="true" />
                  <div className="hd-stat-value">{Number(stats[card.key] || 0).toLocaleString()}</div>
                  <div className="hd-stat-label">{card.label}</div>
                  {isTotalRetreats ? (
                    <div className="hd-stat-hint hd-stat-split">
                      <strong className="hd-stat-active">
                        {Number(stats.activeRetreats || 0).toLocaleString()} Active
                      </strong>
                      <span className="hd-stat-inactive">
                        {Number(stats.inactiveRetreats || 0).toLocaleString()} Inactive
                      </span>
                    </div>
                  ) : (
                    <div className="hd-stat-hint">{card.hint}</div>
                  )}
                </>
              );
              return card.to ? (
                <Link key={card.key} to={card.to} className={`hd-stat is-${card.tone}`}>
                  {body}
                </Link>
              ) : (
                <article key={card.key} className={`hd-stat is-${card.tone}`}>
                  {body}
                </article>
              );
            })}
          </section>

          <section className="hd-section">
            <div className="hd-section-head">
              <div>
                <h2>Trends</h2>
                <p>Choose a month or status to filter Upcoming Retreats.</p>
              </div>
            </div>
            <div className="hd-charts">
              <article className="hd-card">
                <div className="hd-line-head">
                  <div>
                    <h3>Retreats by Start Month</h3>
                    <p className="hd-card-note">
                      January through December {retreatYear}. Hover a point to see retreat names.
                    </p>
                  </div>
                  <span className="hd-line-total">
                    <strong>{retreatYearTotal}</strong>
                    <span>in {retreatYear}</span>
                  </span>
                </div>
                <MonthLineChart
                  series={overview.retreatsByMonth || []}
                  selected={month}
                  onSelect={setMonth}
                />
              </article>
              <article className="hd-card">
                <h3>Retreat Status</h3>
                <p className="hd-card-note">Open Retreats, Closed Retreats, and inactive records.</p>
                <StatusDonut
                  slices={overview.statusMix || []}
                  total={stats.totalRetreats || 0}
                  selected={status}
                  onSelect={setStatus}
                />
              </article>
            </div>
            <div className="hd-charts-secondary">
              <article className="hd-card">
                <div className="hd-line-head">
                  <div>
                    <h3>Needs Closing</h3>
                    <p className="hd-card-note">
                      Recent retreats that have ended and are not closed yet.
                    </p>
                  </div>
                  <Link className="hd-chip" to="/retreat-closure-list">
                    View all
                  </Link>
                </div>
                <NeedsClosingList retreats={overview.needsClosing || []} />
              </article>
              <article className="hd-card">
                <h3>Retreat Languages</h3>
                <p className="hd-card-note">Click a language to open Retreats filtered by that language.</p>
                <div className="hd-lang">
                  {languageRows.length === 0 ? (
                    <p className="hd-card-note">No language data yet.</p>
                  ) : (
                    languageRows.map((item) => (
                      <button
                        key={item.label}
                        type="button"
                        onClick={() =>
                          navigate(
                            `/manageRetreat?language=${encodeURIComponent(item.label)}`,
                          )
                        }
                      >
                        <span>{cap(item.label)}</span>
                        <span className="hd-lang-track">
                          <span
                            className="hd-lang-fill"
                            style={{ width: `${(item.value / languageMax) * 100}%` }}
                          />
                        </span>
                        <strong>{item.value}</strong>
                      </button>
                    ))
                  )}
                </div>
              </article>
            </div>
          </section>

          <section className="hd-section">
            <div className="hd-section-head">
              <div>
                <h2>Upcoming Retreats</h2>
                <p>
                  {visibleRetreats.length} shown
                  {overview.upcoming?.length ? ` of ${overview.upcoming.length}` : ""}. Open a card to
                  work Roster, Directory, Program, and Closing.
                </p>
              </div>
              <div className="hd-filters">
                <label className="hd-search">
                  <i className="mdi mdi-magnify" aria-hidden="true" />
                  <input
                    type="search"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Search parish, location, or retreat ID"
                    aria-label="Search Upcoming Retreats by parish, location, or retreat ID"
                  />
                </label>
                {filtersActive ? (
                  <button
                    type="button"
                    className="hd-chip is-clear"
                    onClick={() => {
                      setQuery("");
                      setMonth("");
                      setStatus("");
                    }}
                  >
                    Clear filters
                  </button>
                ) : null}
              </div>
            </div>

            {visibleRetreats.length === 0 ? (
              <div className="hd-empty">
                {status === "past"
                  ? "Past retreats stay on Manage Retreats. This list shows retreats that are still ahead or underway."
                  : "No Upcoming Retreats match this view."}
              </div>
            ) : (
              <div className="hd-retreats">
                {visibleRetreats.map((retreat) => {
                  const directoryReady =
                    retreat.directoryConfirmed ||
                    retreat.directoryStatus === "PUBLISH" ||
                    retreat.hasDirectoryFile;
                  const programReady =
                    retreat.programConfirmed ||
                    retreat.programStatus === "PUBLISH" ||
                    retreat.hasProgramFile;
                  const links = [
                    {
                      label: "Couples",
                      icon: "mdi-account-multiple",
                      to: `/retreatRoster?retreatId=${retreat.id}&flag=1`,
                    },
                    {
                      label: "Roster",
                      icon: "mdi-format-list-bulleted",
                      to: `/retreatRoster?retreatId=${retreat.id}`,
                      ready: retreat.rosterConfirmed,
                    },
                    {
                      label: "Directory",
                      icon: "mdi-folder-open",
                      to: `/directory/${retreat.id}`,
                      ready: directoryReady,
                    },
                    {
                      label: "Program",
                      icon: "mdi-calendar-multiselect",
                      to: `/program/${retreat.id}`,
                      ready: programReady,
                    },
                    {
                      label: "Closing",
                      icon: "mdi-check-circle-outline",
                      to: "/retreat-closure-list",
                      ready: retreat.retreatCloseStatus,
                    },
                    {
                      label: "Priests",
                      icon: "mdi-account-tie",
                      to: "/priest-list",
                    },
                    {
                      label: "Log Report",
                      icon: "mdi-text-box-outline",
                      to: "/logreport",
                    },
                  ];
                  const dateParts = startDateParts(retreat.dateFrom);
                  const readyCount = [
                    retreat.rosterConfirmed,
                    directoryReady,
                    programReady,
                    retreat.retreatCloseStatus,
                  ].filter(Boolean).length;
                  return (
                    <article key={retreat.id} className="hd-retreat">
                      <div className="hd-retreat-top">
                        <div className="hd-retreat-date" aria-hidden="true">
                          <span>{dateParts.month}</span>
                          <strong>{dateParts.day}</strong>
                        </div>
                        <div className="hd-retreat-copy">
                          <h3>
                            <Link to={`/retreatRoster?retreatId=${retreat.id}`}>
                              {retreatTitle(retreat)}
                            </Link>
                          </h3>
                          <p className="hd-retreat-meta">
                            <i className="mdi mdi-map-marker-outline" aria-hidden="true" />
                            {retreat.parishCity || "Location not set"}
                            <i className="mdi mdi-calendar-blank-outline" aria-hidden="true" />
                            {formatDateRange(retreat.dateFrom, retreat.dateTo)}
                          </p>
                        </div>
                        <div className="hd-badges">
                          <span className={`hd-badge is-${String(retreat.status || "").toLowerCase()}`}>
                            {retreat.status || "Status"}
                          </span>
                          {retreat.retreatCloseStatus ? (
                            <span className="hd-badge is-closed">Closed</span>
                          ) : null}
                          {retreat.freeze === "FREEZE" ? (
                            <span className="hd-badge is-frozen">Frozen</span>
                          ) : null}
                          {retreat.waitList ? <span className="hd-badge is-wait">Wait List</span> : null}
                        </div>
                      </div>
                      <div className="hd-retreat-progress">
                        <span>
                          Prep {readyCount} of 4
                        </span>
                        <span className="hd-retreat-progress-bar" aria-hidden="true">
                          <span style={{ width: `${(readyCount / 4) * 100}%` }} />
                        </span>
                      </div>
                      <div className="hd-metrics">
                        <div className="hd-metric">
                          <i className="mdi mdi-account-heart-outline" aria-hidden="true" />
                          <strong>{retreat.countRetreatant}</strong>
                          <span>Retreatants</span>
                        </div>
                        <div className="hd-metric">
                          <i className="mdi mdi-account-group-outline" aria-hidden="true" />
                          <strong>{retreat.countTeam}</strong>
                          <span>Team</span>
                        </div>
                        <div className="hd-metric">
                          <i className="mdi mdi-clipboard-text-outline" aria-hidden="true" />
                          <strong>{retreat.roasterTotal}</strong>
                          <span>Roster</span>
                        </div>
                        <div className="hd-metric">
                          <i className="mdi mdi-account-tie" aria-hidden="true" />
                          <strong>{retreat.priestCount}</strong>
                          <span>Priests</span>
                        </div>
                      </div>
                      <div className="hd-links">
                        {links.map((link) => (
                          <Link
                            key={link.label}
                            className={`hd-link${link.ready ? " is-ready" : ""}`}
                            to={link.to}
                          >
                            <i className={`mdi ${link.icon}`} aria-hidden="true" />
                            {link.label}
                          </Link>
                        ))}
                        <button
                          type="button"
                          className="hd-link"
                          onClick={() => training?.openGuide("retreat-roster")}
                        >
                          <i className="mdi mdi-play-circle-outline" aria-hidden="true" />
                          Guide Video
                        </button>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </section>

          <section className="hd-section">
            <div className="hd-section-head">
              <div>
                <h2>Guide Videos</h2>
                <p>Play a short walkthrough for Retreats, Rosters, Couples, or Closing.</p>
              </div>
              <Link className="hd-chip" to="/admin-training">
                Open Training Center
              </Link>
            </div>
            <div className="hd-guides">
              {trainingGuides.map((guide) => (
                <button
                  key={guide.id}
                  type="button"
                  className={`hd-guide${guide.featured ? " is-featured" : ""}`}
                  onClick={() => training?.openGuide(guide.id)}
                >
                  <span className="hd-guide-play" aria-hidden="true">
                    <i className={`mdi ${GUIDE_TOPIC_ICONS[guide.topic] || "mdi-play"}`} />
                  </span>
                  <span className="hd-guide-copy">
                    <span className="hd-guide-topic">{guide.topic}</span>
                    <strong>{guide.title}</strong>
                    <p>{guide.description}</p>
                    <span className="hd-guide-foot">
                      <span className="hd-guide-time">
                        <i className="mdi mdi-play-circle-outline" aria-hidden="true" />
                        {guide.lengthLabel}
                      </span>
                      <span className="hd-guide-watch">Watch</span>
                    </span>
                  </span>
                </button>
              ))}
            </div>
          </section>
        </>
      ) : null}
    </div>
  );
};

export default Dashboard;
